// First-party activity tracker — ONE implementation for every surface.
//
// `activityTrackerRuntime` is deliberately SELF-CONTAINED: it references
// nothing outside its own body, so the published static site can embed it as
// `(${activityTrackerRuntime.toString()})(window, cfg)` (see
// `activityTrackerScript` in ../scripts.ts) while the React hosts (Run view,
// SPA site viewer, builder, downloaded runtime) call the very same function.
// Do not import anything into it or hoist a helper out of it.
//
// What it does, cheaply:
//   * keeps events in memory and sends them as one batch every `flushMs`
//     (default 15 s), when the batch is full, and when the tab is hidden or
//     the page unloads (fetch keepalive / sendBeacon) — never one request per
//     click;
//   * a persistent first-party visitor id (localStorage) and a session id that
//     rotates after 30 idle minutes; landing referrer + UTM per session;
//   * time on page/screen measured as VISIBLE time, plus "engaged" time
//     (visible with an interaction in the last minute), and max scroll depth;
//     reported as `page_leave` deltas, so a tab closed without an unload
//     event still has its time counted up to the last hide;
//   * optional auto-capture of clicks (outbound / download / CTA) and form
//     submissions (names only, never values).
// The SERVER decides who the user is; nothing here asserts identity.

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface ActivityTrackerConfig {
  /** Collect URL (absolute, or relative to the page's origin). */
  endpoint: string
  realm: 'app' | 'site' | 'builder'
  surface: string
  owner?: string
  project?: string
  tenant?: string
  flushMs?: number
  maxBatch?: number
  sessionTimeoutMin?: number
  /** Static pages: record a page_view on load (name = pageName or path). */
  autoPageView?: boolean
  pageName?: string
  captureClicks?: boolean
  captureScroll?: boolean
  captureForms?: boolean
  /** 'banner' — a cookie-banner decision denying analytics drops the
   *  persistent visitor id (events still counted). 'none' — always an id. */
  consent?: 'banner' | 'none'
  storagePrefix?: string
  /** fetch credentials mode. Same-origin hosts send their session cookie. */
  credentials?: 'include' | 'same-origin' | 'omit'
  /** Extra request headers (a project user's bearer). React hosts only. */
  headers?: () => Record<string, string>
}

export interface ActivityApi {
  page(name: string, fields?: Record<string, unknown>): void
  track(event: string, name?: string | null, fields?: Record<string, unknown>): void
  setContext(partial: Partial<ActivityTrackerConfig>): void
  flush(final?: boolean): void
  disable(): void
  readonly enabled: boolean
}

export function activityTrackerRuntime(w: any, cfg: any): any {
  if (!w || !w.document) return null
  if (w.__twastaActivity) {
    w.__twastaActivity.setContext(cfg || {})
    return w.__twastaActivity
  }
  var doc = w.document
  var nav = w.navigator || {}
  var C: any = {}
  var k: any
  var defaults: any = {
    flushMs: 15000, maxBatch: 40, sessionTimeoutMin: 30, autoPageView: false,
    captureClicks: true, captureScroll: true, captureForms: true, consent: 'banner',
    storagePrefix: 'tw_a', credentials: 'same-origin', surface: 'web', realm: 'site',
  }
  for (k in defaults) C[k] = defaults[k]
  for (k in (cfg || {})) if (cfg[k] !== undefined) C[k] = cfg[k]

  var MAX_QUEUE = 500
  var KNOWN = ['name', 'obj_type', 'label', 'path', 'title', 'view_id', 'duration_ms', 'engaged_ms',
    'scroll_pct', 'status', 'engine', 'model', 'tokens_in', 'tokens_out', 'thread_id']
  var queue: any[] = []
  var disabled = false
  var timer: any = null
  var engageTimer: any = null
  var view: any = null
  var lastInteraction = 0
  var sending = false

  function now() { return Date.now() }
  function rid() {
    try { if (w.crypto && w.crypto.randomUUID) return w.crypto.randomUUID().replace(/-/g, '') } catch (e) { /* fall through */ }
    var s = ''
    for (var i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16)
    return s
  }
  function lsGet(key: string) { try { return w.localStorage.getItem(C.storagePrefix + '_' + key) } catch (e) { return null } }
  function lsSet(key: string, v: string) { try { w.localStorage.setItem(C.storagePrefix + '_' + key, v) } catch (e) { /* storage blocked */ } }
  function lsDel(key: string) { try { w.localStorage.removeItem(C.storagePrefix + '_' + key) } catch (e) { /* storage blocked */ } }
  function offKey() { return 'off_' + (C.owner || '') + '_' + (C.project || '') + '_' + C.realm }

  // ── consent ────────────────────────────────────────────────────────
  function consentAllowsId() {
    if (C.consent === 'none') return true
    var state: any = null
    try {
      if (w.pgConsent && typeof w.pgConsent.get === 'function') state = w.pgConsent.get()
      else state = JSON.parse(w.localStorage.getItem('pg_consent') || 'null')
    } catch (e) { state = null }
    if (!state || typeof state !== 'object') return true      // no decision recorded
    if (state.analytics === false) return false
    if (state.analytics === undefined && state.all === false) return false
    return true
  }
  function visitorId() {
    if (!consentAllowsId()) { lsDel('vid'); return undefined }
    var v = lsGet('vid')
    if (!v) { v = rid(); lsSet('vid', v) }
    return v
  }

  // ── session (30-min idle rotation, landing acquisition) ────────────
  function utm() {
    var out: any = {}
    try {
      var q = new w.URLSearchParams(w.location.search || '')
      var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      for (var i = 0; i < keys.length; i++) { var v = q.get(keys[i]); if (v) out[keys[i]] = v }
    } catch (e) { /* no URLSearchParams */ }
    return out
  }
  function session() {
    var s: any = null
    try { s = JSON.parse(lsGet('ses') || 'null') } catch (e) { s = null }
    var t = now()
    var fresh = !s || !s.id || (t - (s.last || 0)) > C.sessionTimeoutMin * 60000
    if (fresh) {
      var ref = ''
      try { ref = doc.referrer || '' } catch (e) { ref = '' }
      try { if (ref && new w.URL(ref).host === w.location.host) ref = '' } catch (e) { /* keep */ }
      s = { id: rid(), last: t, ref: ref, utm: utm(), started: t }
    }
    s.last = t
    lsSet('ses', JSON.stringify(s))
    if (fresh) push('session_start', { path: safePath(), title: doc.title || undefined }, s)
    return s
  }
  function safePath() {
    try { return (w.location.pathname || '') + (w.location.search || '') } catch (e) { return '' }
  }

  // ── queue + transport ──────────────────────────────────────────────
  function push(event: string, fields: any, ses?: any) {
    if (disabled) return
    // The session is stamped on EACH event: a batch can straddle a session
    // rollover, and the events before it belong to the old one.
    var s = ses || session()
    var e: any = { event: event, ts: now(), session_id: s && s.id }
    var extra: any = null
    for (var key in (fields || {})) {
      var val = fields[key]
      if (val === undefined || val === null || val === '') continue
      if (KNOWN.indexOf(key) >= 0) e[key] = val
      else { extra = extra || {}; extra[key] = val }
    }
    if (extra) { try { e.props = JSON.stringify(extra).slice(0, 4000) } catch (x) { /* unserialisable */ } }
    queue.push(e)
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE)
    if (queue.length >= C.maxBatch) flush(false)
  }
  function envelope(events: any[]) {
    var s: any = null
    try { s = JSON.parse(lsGet('ses') || 'null') } catch (e) { s = null }
    var scr = ''
    var vp = ''
    try { scr = w.screen.width + 'x' + w.screen.height } catch (e) { scr = '' }
    try { vp = w.innerWidth + 'x' + w.innerHeight } catch (e) { vp = '' }
    var tz = ''
    try { tz = w.Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (e) { tz = '' }
    var body: any = {
      realm: C.realm, owner: C.owner || undefined, project: C.project || undefined,
      tenant: C.tenant || undefined, surface: C.surface, sent_at: now(),
      visitor_id: visitorId(), session_id: s && s.id, screen: scr, viewport: vp,
      timezone: tz, language: nav.language || undefined,
      referrer: (s && s.ref) || undefined, events: events,
    }
    var u = (s && s.utm) || {}
    for (var key in u) body[key] = u[key]
    return JSON.stringify(body)
  }
  function headers() {
    var h: any = { 'Content-Type': 'text/plain;charset=UTF-8' }
    if (typeof C.headers === 'function') {
      try { var extra = C.headers() || {}; for (var key in extra) if (extra[key]) h[key] = extra[key] } catch (e) { /* host header provider failed */ }
    }
    return h
  }
  function handleReply(r: any) {
    if (!r) return
    if (r.status === 404 || r.status === 401) { disabled = true; stopTimers(); return }
    if (r.json) {
      r.json().then(function (j: any) {
        if (j && j.enabled === false) {
          disabled = true; queue = []; stopTimers()
          try { w.sessionStorage.setItem(C.storagePrefix + '_' + offKey(), '1') } catch (e) { /* storage blocked */ }
        }
      }).catch(function () { /* not JSON */ })
    }
  }
  function flush(final: boolean) {
    if (disabled || !queue.length || !C.endpoint) return
    if (sending && !final) return
    var batch = queue.splice(0, C.maxBatch)
    var body = envelope(batch)
    var h = headers()
    var hasAuth = false
    for (var key in h) if (key.toLowerCase() === 'authorization') hasAuth = true
    // An unloading page: sendBeacon is the most reliable carrier, but it
    // cannot carry headers — a page whose identity rides a header uses
    // keepalive fetch instead.
    if (final && !hasAuth && nav.sendBeacon) {
      try {
        if (nav.sendBeacon(C.endpoint, new w.Blob([body], { type: 'text/plain;charset=UTF-8' }))) {
          if (queue.length) flush(true)
          return
        }
      } catch (e) { /* fall back to fetch */ }
    }
    if (typeof w.fetch !== 'function') return
    sending = true
    try {
      w.fetch(C.endpoint, {
        method: 'POST', body: body, keepalive: body.length < 60000, headers: h,
        credentials: C.credentials, mode: 'cors',
      }).then(function (r: any) {
        sending = false
        handleReply(r)
        if (r && r.status === 429) return            // back off until the next tick
        if (queue.length && !final) flush(false)
      }).catch(function () {
        sending = false
        // Offline / blocked: put the batch back (bounded) for the next tick.
        queue = batch.concat(queue).slice(0, MAX_QUEUE)
      })
    } catch (e) {
      sending = false
    }
    if (final && queue.length) flush(true)
  }

  // ── views + time ───────────────────────────────────────────────────
  function visibleNow() { try { return doc.visibilityState !== 'hidden' } catch (e) { return true } }
  function scrollPct() {
    try {
      var el = doc.documentElement
      var h = Math.max(el.scrollHeight, doc.body ? doc.body.scrollHeight : 0)
      if (h <= w.innerHeight + 4) return 100
      return Math.min(100, Math.round(((w.scrollY || el.scrollTop || 0) + w.innerHeight) / h * 100))
    } catch (e) { return 0 }
  }
  function reportTime(final: boolean) {
    if (!view) return
    var t = now()
    if (view.visibleSince) { view.visibleMs += t - view.visibleSince; view.visibleSince = visibleNow() && !final ? t : 0 }
    // Engagement is counted in 5-second ticks; it can never exceed the time
    // the page was actually visible.
    if (view.engagedMs > view.visibleMs) view.engagedMs = view.visibleMs
    var d = view.visibleMs - view.reportedMs
    var g = view.engagedMs - view.reportedEngaged
    if (d <= 0 && !final) return
    view.reportedMs = view.visibleMs
    view.reportedEngaged = view.engagedMs
    push('page_leave', {
      name: view.name, obj_type: view.obj_type, view_id: view.id, path: view.path,
      duration_ms: Math.max(0, d), engaged_ms: Math.max(0, g),
      scroll_pct: C.captureScroll ? view.scroll : undefined,
    })
  }
  function page(name: string, fields?: any) {
    if (disabled) return
    reportTime(true)
    var f = fields || {}
    view = {
      id: rid(), name: name || safePath(), obj_type: f.obj_type, path: f.path || safePath(),
      visibleSince: visibleNow() ? now() : 0, visibleMs: 0, engagedMs: 0,
      reportedMs: 0, reportedEngaged: 0, scroll: C.captureScroll ? scrollPct() : undefined,
    }
    lastInteraction = now()
    var ev: any = { name: view.name, view_id: view.id, path: view.path, title: f.title || doc.title || undefined }
    for (var key in f) if (ev[key] === undefined) ev[key] = f[key]
    push('page_view', ev)
  }
  function interact() { lastInteraction = now() }
  function onScroll() {
    interact()
    if (view && C.captureScroll) { var p = scrollPct(); if (p > (view.scroll || 0)) view.scroll = p }
  }

  // ── auto-capture ───────────────────────────────────────────────────
  function onClick(e: any) {
    interact()
    if (!C.captureClicks || disabled) return
    var el = e.target && e.target.closest ? e.target.closest('a,button,[role=button],[data-track]') : null
    if (!el) return
    var label = (el.getAttribute('data-track') || el.getAttribute('aria-label') || el.textContent || '')
      .replace(/\s+/g, ' ').trim().slice(0, 120)
    var href = el.getAttribute('href') || ''
    var ev = 'click'
    if (href && !/^(#|javascript:)/i.test(href)) {
      try {
        var u = new w.URL(href, w.location.href)
        if (el.hasAttribute('download') || /\.(pdf|zip|docx?|xlsx?|pptx?|csv|mp4|mp3|dmg|exe|apk)(\?|$)/i.test(u.pathname)) ev = 'download'
        else if (u.host && u.host !== w.location.host) ev = 'outbound'
      } catch (x) { /* unparsable href */ }
    }
    push(ev, { name: view ? view.name : safePath(), label: label || href, href: href || undefined })
  }
  function onSubmit(e: any) {
    if (!C.captureForms || disabled) return
    var f = e.target
    if (!f || f.tagName !== 'FORM') return
    push('form_submit', {
      name: view ? view.name : safePath(),
      label: f.getAttribute('data-pg-label') || f.getAttribute('data-pg-form') || f.getAttribute('name') || f.id || 'form',
    })
  }
  function onVisibility() {
    if (!view) return
    if (visibleNow()) { view.visibleSince = now(); return }
    reportTime(false)
    flush(true)
  }
  function onHide() { reportTime(true); flush(true) }

  function startTimers() {
    stopTimers()
    timer = w.setInterval(function () { flush(false) }, C.flushMs)
    engageTimer = w.setInterval(function () {
      if (view && visibleNow() && now() - lastInteraction < 60000) view.engagedMs += 5000
    }, 5000)
  }
  function stopTimers() {
    if (timer) { w.clearInterval(timer); timer = null }
    if (engageTimer) { w.clearInterval(engageTimer); engageTimer = null }
  }

  try { if (w.sessionStorage.getItem(C.storagePrefix + '_' + offKey()) === '1') disabled = true } catch (e) { /* storage blocked */ }
  if (nav.webdriver) disabled = true   // automation (our own page capture, test rigs)

  var api: any = {
    page: page,
    track: function (event: string, name?: string, fields?: any) {
      var f: any = {}
      for (var key in (fields || {})) f[key] = fields[key]
      if (name) f.name = name
      else if (view && f.name === undefined) f.name = view.name
      push(event, f)
    },
    setContext: function (partial: any) {
      var changing = partial && ((partial.project !== undefined && partial.project !== C.project)
        || (partial.owner !== undefined && partial.owner !== C.owner)
        || (partial.realm !== undefined && partial.realm !== C.realm))
      if (changing) { reportTime(true); flush(true); view = null }
      for (var key in (partial || {})) if (partial[key] !== undefined) C[key] = partial[key]
      if (changing) {
        disabled = !!nav.webdriver
        try { if (w.sessionStorage.getItem(C.storagePrefix + '_' + offKey()) === '1') disabled = true } catch (e) { /* storage blocked */ }
        if (!disabled && !timer) startTimers()
      }
    },
    flush: function (final?: boolean) { flush(!!final) },
    disable: function () { disabled = true; queue = []; stopTimers() },
  }
  Object.defineProperty(api, 'enabled', { get: function () { return !disabled } })
  w.__twastaActivity = api

  doc.addEventListener('click', onClick, true)
  doc.addEventListener('submit', onSubmit, true)
  doc.addEventListener('keydown', interact, true)
  doc.addEventListener('visibilitychange', onVisibility)
  w.addEventListener('scroll', onScroll, { passive: true })
  w.addEventListener('mousemove', interact, { passive: true })
  w.addEventListener('touchstart', interact, { passive: true })
  w.addEventListener('pagehide', onHide)
  if (!disabled) startTimers()
  if (C.autoPageView) page(C.pageName || safePath(), {})
  return api
}
